/**
 * Enhanced Workflow Components with Unique Properties
 *
 * Each component is tailored to its specific workflow type with:
 * - Unique data structures and fields
 * - Specialized UI interactions
 * - Type-specific validations
 * - Custom visualizations
 * - Integrated Mantine UI and Tabulator features
 */

// Import all components for internal use
import React, { type ComponentType } from 'react';
import ActorDescriptorEnhanced from './ActorDescriptorEnhanced';
import AdminOnlyWrapper from './AdminOnlyWrapper';
import { CommunicationManager } from './CommunicationManager';
import ComplexProcessManager from './ComplexProcessManager';
import ComplexWorkflowVisualizer from './ComplexWorkflowVisualizer';

import ComprehensiveDashboard from './ComprehensiveDashboard';
import ContextEnhanced from './ContextEnhanced';
import DomainConfigurationManager from './DomainConfigurationManager';
import {
  DOMAIN_PROCESSES,
  getDomains,
  getProcessById,
  getProcessesByDomain,
} from './DomainProcessLibrary';
import EnhancedComponentProvider from './EnhancedComponentProvider';
import EscalationEnhanced from './EscalationEnhanced';
import ExecutionResultEnhanced from './ExecutionResultEnhanced';
import { GraphQLLifecycleDashboard } from './GraphQLDashboardWidgets';
import InstanceEnhanced from './InstanceEnhanced';
import IntegrationEnhanced from './IntegrationEnhanced';
import JobStatusEnhanced from './JobStatusEnhanced';
import { LifecycleDashboard } from './LifecycleDashboardWidgets';
import OptimizationRecommendationEnhanced from './OptimizationRecommendationEnhanced';
import PolicyEnhanced from './PolicyEnhanced';
import ProcessVisualEditor from './ProcessVisualEditor';
import ProductionMonitoringDashboard from './ProductionMonitoringDashboard';
import ProductionSystemHealthDashboard from './ProductionSystemHealthDashboard';
import ResourceEnhanced from './ResourceEnhanced';
import ScheduleEnhanced from './ScheduleEnhanced';
import SecureSystemHealthDashboard from './SecureSystemHealthDashboard';
import SignalEnhanced from './SignalEnhanced';
import SystemHealthDashboard from './SystemHealthDashboard';
import TabulatorLifecycleDashboard from './TabulatorLifecycleDashboard';
import TaskEnhanced from './TaskEnhanced';
import TimelineItemEnhanced from './TimelineItemEnhanced';
import TransitionCatalogEnhanced from './TransitionCatalogEnhanced';
import ValidatedComplexProcessForm from './ValidatedComplexProcessForm';
import ValidatedContextForm from './ValidatedContextForm';
import ValidatedEscalationForm from './ValidatedEscalationForm';
import ValidatedIntegrationForm from './ValidatedIntegrationForm';
import ValidatedJobStatusForm from './ValidatedJobStatusForm';
import ValidatedPolicyForm from './ValidatedPolicyForm';
import ValidatedResourceForm from './ValidatedResourceForm';
import ValidatedScheduleForm from './ValidatedScheduleForm';
import ValidatedSignalForm from './ValidatedSignalForm';
import ValidatedTaskForm from './ValidatedTaskForm';
import WorkflowComponentsController from './WorkflowComponentsController';
import WorkflowComponentsSearch from './WorkflowComponentsSearch';
import WorkflowComponentsTable from './WorkflowComponentsTable';
import WorkflowDefinitionEnhanced from './WorkflowDefinitionEnhanced';
import WorkflowLifecyclePlane from './WorkflowLifecyclePlane';
import WorkflowSearchHub from './WorkflowSearchHub';
import WorkflowSearchMenu from './WorkflowSearchMenu';
import WorkflowShowcase from './WorkflowShowcase';

// Phase 1 FERP Enhanced Components (Life-Critical Safety Framework)
import CircuitBreakerStatusVisualization from './CircuitBreakerStatusVisualization';
import DomainLandingPage from './DomainLandingPage';
import RegulatoryGovernanceDashboard from './RegulatoryGovernanceDashboard';
import UniversalPrimitiveRegistryDashboard from './UniversalPrimitiveRegistryDashboard';

// Phase 3-6 FERP UI/UX Transformation Components
import DynamicConfigurationShowcase from './DynamicConfigurationShowcase';
import MultiDomainRegulatoryInterface from './MultiDomainRegulatoryInterface';
import PrimitiveCompositionDashboard from './PrimitiveCompositionDashboard';
import SafetyComplianceCommandCenter from './SafetyComplianceCommandCenter';

// Re-export all components
// Provider
export { EnhancedComponentProvider };

// Core Engine Types (9)
export {
  ActorDescriptorEnhanced,
  ExecutionResultEnhanced,
  InstanceEnhanced,
  JobStatusEnhanced,
  SignalEnhanced,
  TaskEnhanced,
  TimelineItemEnhanced,
  TransitionCatalogEnhanced,
  WorkflowDefinitionEnhanced,
};

// Governance & Operations Types (6)
export {
  ContextEnhanced,
  EscalationEnhanced,
  IntegrationEnhanced,
  PolicyEnhanced,
  ResourceEnhanced,
  ScheduleEnhanced,
};

// AI Optimization Types (1)
export { OptimizationRecommendationEnhanced };

// Showcase & Search Components
export {
  AdminOnlyWrapper,
  CommunicationManager,
  ComplexProcessManager,
  ComplexWorkflowVisualizer,
  ComprehensiveDashboard,
  DomainConfigurationManager,
  GraphQLLifecycleDashboard,
  LifecycleDashboard,
  ProcessVisualEditor,
  ProductionMonitoringDashboard,
  ProductionSystemHealthDashboard,
  SecureSystemHealthDashboard,
  SystemHealthDashboard,
  TabulatorLifecycleDashboard,
  WorkflowComponentsController,
  WorkflowComponentsSearch,
  WorkflowComponentsTable,
  WorkflowLifecyclePlane,
  WorkflowSearchHub,
  WorkflowSearchMenu,
  WorkflowShowcase,
};

// Phase 1 FERP Enhanced Components (Life-Critical Safety Framework Integration)
export {
  CircuitBreakerStatusVisualization,
  DomainLandingPage,
  RegulatoryGovernanceDashboard,
  UniversalPrimitiveRegistryDashboard,
};

// Phase 3-6 FERP UI/UX Transformation Components (Complete Enterprise Suite)
export {
  DynamicConfigurationShowcase,
  MultiDomainRegulatoryInterface,
  PrimitiveCompositionDashboard,
  SafetyComplianceCommandCenter,
};

// Validated Forms (Production-Ready with GraphQL Integration)
export { default as ValidatedOptimizationRecommendationForm } from './ValidatedOptimizationRecommendationForm';
export {
  ValidatedComplexProcessForm,
  ValidatedContextForm,
  ValidatedEscalationForm,
  ValidatedIntegrationForm,
  ValidatedJobStatusForm,
  ValidatedPolicyForm,
  ValidatedResourceForm,
  ValidatedScheduleForm,
  ValidatedSignalForm,
  ValidatedTaskForm,
};

// Manager Components (Full CRUD with GraphQL Persistence)
export { EscalationManager } from './EscalationManager';
export { IntegrationManager } from './IntegrationManager';
export { PolicyManager } from './PolicyManager';
export { ResourceManager } from './ResourceManager';
export { ScheduleManager } from './ScheduleManager';

// Comprehensive Forms (185+ Field Support, Life-Critical Features)
export { ComprehensiveTaskForm } from './ComprehensiveTaskForm';
export { ComprehensiveTaskFormDemo } from './ComprehensiveTaskFormDemo';

// Task Form Clone / Template / LLM Prefill
export { TaskFormToolbar } from './TaskFormToolbar';
export { SaveTemplateModal } from './SaveTemplateModal';
export { TaskTemplatePicker } from './TaskTemplatePicker';
export { TaskPrefillPanel } from './TaskPrefillPanel';
export type { TaskFormState, TaskFormSetters, BuiltinScenario, FieldManifestEntry } from './taskFormState';
export { BUILTIN_SCENARIOS, TASK_FIELD_MANIFEST, applyFormState, serializeForInitialData } from './taskFormState';

// Domain Process Library
export { DOMAIN_PROCESSES, getDomains, getProcessById, getProcessesByDomain };

// Type definitions for enhanced components
export interface EnhancedComponentProps {
  id?: string;
  data?: any;
  mode?: 'view' | 'edit' | 'create';
  tenant?: string;
  onSave?: (data: any) => void;
  onCancel?: () => void;
  onDelete?: (id: string) => void;
}

// Workflow type names for reference
export type WorkflowTypeName =
  | 'WorkflowDefinition'
  | 'Instance'
  | 'Signal'
  | 'ExecutionResult'
  | 'TimelineItem'
  | 'Task'
  | 'JobStatus'
  | 'ActorDescriptor'
  | 'TransitionCatalog'
  | 'Policy'
  | 'Context'
  | 'Schedule'
  | 'Resource'
  | 'Integration'
  | 'Escalation'
  | 'OptimizationRecommendation';

// Parity: Map every WorkflowTypeName to its enhanced component (1:1)
const ENHANCED_COMPONENTS_INTERNAL = {
  WorkflowDefinition: WorkflowDefinitionEnhanced,
  Instance: InstanceEnhanced,
  Signal: SignalEnhanced,
  ExecutionResult: ExecutionResultEnhanced,
  TimelineItem: TimelineItemEnhanced,
  Task: TaskEnhanced,
  JobStatus: JobStatusEnhanced,
  ActorDescriptor: ActorDescriptorEnhanced,
  TransitionCatalog: TransitionCatalogEnhanced,
  Policy: PolicyEnhanced,
  Context: ContextEnhanced,
  Schedule: ScheduleEnhanced,
  Resource: ResourceEnhanced,
  Integration: IntegrationEnhanced,
  Escalation: EscalationEnhanced,
  OptimizationRecommendation: OptimizationRecommendationEnhanced,
} as const;

// Create adapter components for type safety while maintaining backward compatibility
function createEnhancedAdapter<TProps extends Record<string, any>>(
  Component: ComponentType<TProps>,
): ComponentType<EnhancedComponentProps> {
  return (props: EnhancedComponentProps) => {
    // Extract relevant props and pass through with proper typing
    const adaptedProps = {
      ...props,
      // Ensure core props are passed through
      id: props.id,
      data: props.data,
      mode: props.mode || 'view',
      tenant: props.tenant,
      onSave: props.onSave,
      onCancel: props.onCancel,
      onDelete: props.onDelete,
    } as unknown as TProps;

    return React.createElement(Component, adaptedProps);
  };
}

export const ENHANCED_COMPONENTS: Record<
  WorkflowTypeName,
  ComponentType<EnhancedComponentProps>
> = {
  WorkflowDefinition: createEnhancedAdapter(WorkflowDefinitionEnhanced),
  Instance: createEnhancedAdapter(InstanceEnhanced),
  Signal: createEnhancedAdapter(SignalEnhanced),
  ExecutionResult: createEnhancedAdapter(ExecutionResultEnhanced),
  TimelineItem: createEnhancedAdapter(TimelineItemEnhanced),
  Task: createEnhancedAdapter(TaskEnhanced),
  JobStatus: createEnhancedAdapter(JobStatusEnhanced),
  ActorDescriptor: createEnhancedAdapter(ActorDescriptorEnhanced),
  TransitionCatalog: createEnhancedAdapter(TransitionCatalogEnhanced),
  Policy: createEnhancedAdapter(PolicyEnhanced),
  Context: createEnhancedAdapter(ContextEnhanced),
  Schedule: createEnhancedAdapter(ScheduleEnhanced),
  Resource: createEnhancedAdapter(ResourceEnhanced),
  Integration: createEnhancedAdapter(IntegrationEnhanced),
  Escalation: createEnhancedAdapter(EscalationEnhanced),
  OptimizationRecommendation: createEnhancedAdapter(OptimizationRecommendationEnhanced),
};

type EnhancedComponentKey = keyof typeof ENHANCED_COMPONENTS_INTERNAL;

// Compile-time assertions: coverage and no extras
type AssertEnhancedCoversAll = WorkflowTypeName extends EnhancedComponentKey ? true : never;
type AssertNoExtraEnhanced = EnhancedComponentKey extends WorkflowTypeName ? true : never;
const _assertEnhancedCoversAll: AssertEnhancedCoversAll = true;
const _assertNoExtraEnhanced: AssertNoExtraEnhanced = true;

// Runtime helper (optional use in diagnostics/tests)
export function isEnhancedParityComplete(): boolean {
  return Object.keys(ENHANCED_COMPONENTS).length === 16;
}

// Metadata for each workflow type
export const WorkflowMetadata = {
  WorkflowDefinition: {
    title: 'Workflow Definition',
    description: 'Design state machines with transitions, guards, and actions',
    icon: '🔄',
    color: 'blue',
    features: ['State Management', 'Transitions', 'Guards', 'Actions', 'Version Control'],
  },
  Instance: {
    title: 'Workflow Instance',
    description: 'Track running workflow instances and their current state',
    icon: '▶️',
    color: 'green',
    features: ['Status Tracking', 'Context Management', 'Timeline View', 'State History'],
  },
  Signal: {
    title: 'Signal',
    description: 'Send events and signals to workflow instances',
    icon: '📡',
    color: 'orange',
    features: ['Event Routing', 'Correlation', 'Payload Management', 'Broadcasting'],
  },
  ExecutionResult: {
    title: 'Execution Result',
    description: 'Analyze workflow execution outcomes and metrics',
    icon: '📊',
    color: 'purple',
    features: ['Performance Metrics', 'Error Analysis', 'Success Tracking', 'Comparisons'],
  },
  TimelineItem: {
    title: 'Timeline Item',
    description: 'View chronological sequence of workflow events',
    icon: '⏱️',
    color: 'cyan',
    features: ['Event Sequence', 'Timestamps', 'Audit Trail', 'Filtering'],
  },
  Task: {
    title: 'Task',
    description: 'Manage workflow tasks with assignments and priorities',
    icon: '✅',
    color: 'teal',
    features: ['Assignment', 'Priority', 'Due Dates', 'Dependencies', 'Progress'],
  },
  JobStatus: {
    title: 'Job Status',
    description: 'Monitor background job execution and resource usage',
    icon: '⚙️',
    color: 'gray',
    features: ['Progress Tracking', 'Logs', 'Resource Usage', 'Queue Management'],
  },
  ActorDescriptor: {
    title: 'Actor Descriptor',
    description: 'Define workflow participants with roles and permissions',
    icon: '👤',
    color: 'indigo',
    features: ['Role Management', 'Permissions', 'Capabilities', 'Metadata'],
  },
  TransitionCatalog: {
    title: 'Transition Catalog',
    description: 'Catalog of available transitions, guards, and actions',
    icon: '📚',
    color: 'pink',
    features: ['Transition Library', 'Guard Templates', 'Action Registry', 'Reusability'],
  },
  Policy: {
    title: 'Policy',
    description: 'Business rules and compliance policies with enforcement',
    icon: '🛡️',
    color: 'red',
    features: ['Rule Engine', 'Compliance', 'CEL Expressions', 'Testing', 'Versioning'],
  },
  Context: {
    title: 'Context',
    description: 'Variable management and execution context configuration',
    icon: '🎯',
    color: 'yellow',
    features: ['Variable Scoping', 'State Management', 'Inheritance', 'Configuration'],
  },
  Schedule: {
    title: 'Schedule',
    description: 'Time-based workflow orchestration with cron and calendars',
    icon: '📅',
    color: 'lime',
    features: ['Cron Builder', 'Timezone Support', 'Calendar View', 'Triggers', 'History'],
  },
  Resource: {
    title: 'Resource',
    description: 'Resource allocation with limits and utilization tracking',
    icon: '📦',
    color: 'brown',
    features: ['Allocation', 'Limits', 'Utilization', 'Reservation', 'Monitoring'],
  },
  Integration: {
    title: 'Integration',
    description: 'External system integrations with APIs and webhooks',
    icon: '🔌',
    color: 'violet',
    features: ['API Configuration', 'Webhooks', 'Authentication', 'Data Mapping', 'Testing'],
  },
  Escalation: {
    title: 'Escalation',
    description: 'Escalation paths with triggers and notifications',
    icon: '🚨',
    color: 'dark',
    features: ['Escalation Levels', 'Triggers', 'Notifications', 'SLA Management', 'Routing'],
  },
  OptimizationRecommendation: {
    title: 'AI Optimization',
    description: 'AI-powered workflow optimization with life-critical safety validation',
    icon: '🧠',
    color: 'grape',
    features: [
      'AI Analysis',
      'Safety Validation',
      'Regulatory Compliance',
      'Impact Assessment',
      'Approval Workflows',
    ],
  },
} as const;
